import { Box, Grid, MenuItem, Stack, TextField, Autocomplete, Chip } from '@mui/material'
import Header from 'components/Header'
import { BasePathProvider } from 'components/Providers'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { SaveAltIcon } from 'components/Icons'
import { SubmitButton } from 'components/Buttons'
import { createCampusEnrollment, updateCampusEnrollment } from 'store/person/enrollment'
import { useDispatch, useSelector } from 'react-redux'

const initialValues = {
  campusId: null,
  startDate: null,
  endDate: null,
  jobTitleId: null,
  permissionId: null,
  roles: [],
}

export default function EnrollmentsForm({ campusEnrollments, data, campuses, districtTypes, enrollmentId }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { personId } = useParams()
  const basePath = `/people/staff/${personId}/campus/`
  const roles = useSelector(state => state.lookup.roles).rows

  const schema = yup.object().shape({
    campusId: yup.number().required(t('required')),
  })

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: data
      ? {
        campusId: data?.campus?.id,
        districtType: data?.campus?.district?.type,
        startDate: data?.startDate ? dayjs(data.startDate) : null,
        endDate: data?.endDate ? dayjs(data.endDate) : null,
        roleIds: data?.campusEnrollmentRoles?.map(r => r?.role?.id) || [],
      }
      : { ...initialValues, startDate: dayjs(new Date().now) },
  })

  const onSubmit = async formData => {
    if (data?.id && enrollmentId !== 'new') {
      const res = await dispatch(
        updateCampusEnrollment({
          personId: +personId,
          id: +data?.id,
          startDate: formData.startDate ? dayjs(formData.startDate).format('YYYY-MM-DD') : null,
          endDate: formData.endDate ? dayjs(formData.endDate).format('YYYY-MM-DD') : null,
          roleIds: formData.roleIds,
        }),
      )
      if (res.payload) {
        navigate(basePath)
      }
    } else {
      const res = await dispatch(
        createCampusEnrollment({
          personId: +personId,
          campusId: formData.campusId,
          startDate: dayjs(formData.startDate).format('YYYY-MM-DD'),
          endDate: null,
          roleIds: formData.roleIds,
        }),
      )
      if (res.payload) {
        navigate(basePath)
      }
    }
  }

  const onErrors = errors => console.log(errors)

  const selectedDistrictType = useWatch({ control, name: 'districtType' })

  if (!campuses) return null

  let campusList = []

  if (data?.id && data?.id !== 'new') {
    campusList = campuses
  } else {
    campuses.forEach(camp => {
      const temp = campusEnrollments.find(ce => ce.campusId === camp.id && ce.endDate === null)
      if (!temp) campusList.push(camp)
    })
  }

  const filteredCampuses = selectedDistrictType
    ? campusList.filter(c => c.type === selectedDistrictType)
    : campusList

  const filteredRoles = selectedDistrictType
    ? (roles || []).filter(r => !r.types?.length || r.types.includes(selectedDistrictType))
    : (roles || [])

  return (
    <BasePathProvider value={`${basePath}`}>
      <Box
        data-component="CampusEnrollmentForm"
        component="form"
        onSubmit={handleSubmit(onSubmit, onErrors)}
        noValidate
        sx={{ overflowY: 'scroll', border: 'none' }}
      >
        <Header title={t('Campus Information')} small close borderBottom />
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12} md={12}>
            <Controller
              name="districtType"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={districtTypes || []}
                  getOptionLabel={option => option.name || ''}
                  value={districtTypes?.find(r => r.code === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.code || null)
                    setValue('campusId', null)
                    setValue('roleIds', [])
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('District Type')}
                      size="small"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                  disabled={enrollmentId !== 'new'}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name="campusId"
              control={control}
              render={({ field: { ...field }, fieldState }) => {
                return (
                  <TextField
                    disabled={data ? true : false || !selectedDistrictType}
                    size="small"
                    fullWidth
                    select
                    label={t('Campus')}
                    error={!!fieldState.error}
                    {...field}

                  >
                    {filteredCampuses.length === 0
                      ? <MenuItem disabled value="">{t('No options')}</MenuItem>
                      : filteredCampuses.map(item => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name="roleIds"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  options={filteredRoles}
                  getOptionLabel={option => option.name || ''}
                  value={(roles || []).filter(r => field.value?.includes(r.id))}
                  onChange={(_, newValue) => field.onChange(newValue.map(r => r.id))}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={!selectedDistrictType}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip
                        key={option.id}
                        label={option.name}
                        size="small"
                        variant="outlined"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('User Role')}
                      size="small"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}

                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => {
                const datePickerSx = fieldState.error ? { label: { color: 'red' } } : {}
                return (
                  <DatePicker
                    sx={datePickerSx}
                    slotProps={{
                      actionBar: {
                        actions: ['clear'],
                      },
                      textField: { size: 'small' },
                      field: {
                        fullWidth: true,
                        InputLabelProps: { shrink: true },
                      },
                    }}
                    label={t('Start Date')}
                    InputLabelProps={{ shrink: true }}
                    {...field}
                    value={field.value && dayjs(field.value)}
                  />
                )
              }}
            />
          </Grid>
          {enrollmentId !== 'new' && (
            <Grid item xs={12} md={12}>
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState }) => {
                  const datePickerSx = fieldState.error ? { label: { color: 'red' } } : {}
                  return (
                    <DatePicker
                      sx={datePickerSx}
                      slotProps={{
                        actionBar: {
                          actions: ['clear'],
                        },
                        textField: { size: 'small' },
                        field: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                        },
                      }}
                      label={t('End Date')}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value && dayjs(field.value)}
                    />
                  )
                }}
              />
            </Grid>
          )}

        </Grid>
        <Stack justifyContent={'flex-end'} flexDirection={'row'} sx={{ mr: 2 }}>
          <SubmitButton
            color="primary"
            icon={<SaveAltIcon />}
            title={t('SAVE')}
            onClick={handleSubmit(onSubmit, onErrors)}
          />
        </Stack>
      </Box>
    </BasePathProvider>
  )
}
